import { useQuery } from "@tanstack/react-query";
import {
    topTournamentPerformancesQueryOptions,
    type TopTournamentsResponse,
    type TournamentPerformance,
    type GoalAssistPerformance,
} from "@/api/kpis";
import { LeaderboardTable, type LeaderboardRow } from "./LeaderboardTable";
import { useSelectedTeam } from "@/context/teamContext";

interface TournamentPerformanceGridProps {
    selectedTournament?: number;
}

const METRICS = [
    { key: "total" as const, label: "Total KPI", valueLabel: "Total KPI", isKpi: false },
    { key: "average" as const, label: "Average KPI Per Match", valueLabel: "Avg KPI", isKpi: true },
    { key: "goals" as const, label: "Goals", valueLabel: "Goals", isKpi: false },
    { key: "assists" as const, label: "Assists", valueLabel: "Assists", isKpi: false },
    { key: "blockedShots" as const, label: "Blocked Shots", valueLabel: "Blocks", isKpi: false },
];

function buildRows(apiData: TopTournamentsResponse | undefined, metricKey: string): LeaderboardRow[] {
    if (!apiData) return [];

    let performances: (TournamentPerformance | GoalAssistPerformance)[];
    switch (metricKey) {
        case "total":
            performances = apiData.byTotal;
            break;
        case "average":
            performances = apiData.byAverage;
            break;
        case "goals":
            performances = apiData.goals;
            break;
        case "assists":
            performances = apiData.assists;
            break;
        case "blockedShots":
            performances = apiData.blockedShots;
            break;
        default:
            performances = [];
    }

    const getValue = (p: TournamentPerformance | GoalAssistPerformance) =>
        "scores" in p ? p.scores : metricKey === "total" ? p.total : p.average;

    const sorted = [...performances]
        .filter((p) => getValue(p) > 0)
        .sort((a, b) => getValue(b) - getValue(a) || b.matchesPlayed - a.matchesPlayed);

    let rank = 1;
    return sorted.map((p, i) => {
        if (i > 0 && getValue(sorted[i]) !== getValue(sorted[i - 1])) {
            rank = i + 1;
        } else if (
            i > 0 &&
            getValue(sorted[i]) === getValue(sorted[i - 1]) &&
            sorted[i].matchesPlayed !== sorted[i - 1].matchesPlayed
        ) {
            rank = i + 1;
        }
        return {
            rank,
            playerId: p.playerId,
            firstName: p.firstName,
            lastName: p.lastName,
            tournamentName: p.tournamentName,
            matchesPlayed: p.matchesPlayed,
            value: getValue(p),
        };
    });
}

const TournamentPerformanceGrid = ({ selectedTournament }: TournamentPerformanceGridProps) => {
    const { teamId } = useSelectedTeam();
    const { data: apiData, isLoading } = useQuery(topTournamentPerformancesQueryOptions(selectedTournament, teamId));

    return (
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-3">
            {METRICS.map((metric) => {
                const rows = buildRows(apiData, metric.key);
                return (
                    <div key={metric.key} className="rounded-xl overflow-hidden">
                        <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title">{metric.label}</h3>
                        <LeaderboardTable
                            data={rows}
                            isLoading={isLoading && rows.length === 0}
                            valueLabel={metric.valueLabel}
                            isKpi={metric.isKpi}
                            compact
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default TournamentPerformanceGrid;
